import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { AuthService } from '@fluxonos/features/user/services/auth.service';
import { CustomerOperatorDeviceService } from '@fluxonos/features/customer/services/customer-operator-device.service';
import { IOperatorMetricsPayload, IOperatorStatusPayload } from '@fluxonos/fluxonos-dto';
import { environment } from '@fluxonos/environments/environment';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class CustomerOperatorDeviceSocketEventService {
  private readonly _authService = inject(AuthService);
  private readonly _customerOperatorDeviceService = inject(CustomerOperatorDeviceService);

  private _socket: Socket | undefined;
  private _connect$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Guards against refresh storms while socket.io keeps retrying after an auth-rejected connect.
  private _authRefreshInFlight = false;

  private _data = signal<any | null>(null);
  private _data$ = toObservable(this._data);

  private _operatorStatus$ = new BehaviorSubject<IOperatorStatusPayload | null>(null);
  private _operatorHeartbeat$ = new BehaviorSubject<IOperatorStatusPayload | null>(null);
  private _operatorMetrics$ = new BehaviorSubject<IOperatorMetricsPayload | null>(null);

  private _initWs(): void {
    if (this._socket) {
      return;
    }
    this._socket = io(`${environment.apiUrl}/operator-client-events`, {
      // auth as a CALLBACK so every (re)connect attempt reads the CURRENT access token — not the
      // one captured at setup. Without this, socket.io's reconnect keeps retrying with the token
      // it first had, which is dead ~10 min later, and the socket never comes back.
      auth: (cb: (data: object) => void) => {
        const token = this._authService.token;
        cb({
          authorization: token?.accessToken ? `bearer ${token.accessToken}` : '',
          customerOperatorDeviceId: this._customerOperatorDeviceService.selectedCustomerOperatorDevice?.id
        });
      },
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 15000,
      reconnectionAttempts: Infinity,
      transports: ['websocket'],
      secure: true
    });

    this._socket.on('connect', () => {
      this._authRefreshInFlight = false;
      this._connect$.next(true);
    });

    this._socket.on('disconnect', () => {
      // socket.io (reconnection:true) retries automatically; the auth callback supplies a fresh
      // token on each attempt, so no manual reconnect scheduling is needed here.
      this._connect$.next(false);
    });

    this._socket.on('connect_error', () => {
      this._connect$.next(false);
      // Most common cause: expired/invalid access token. Refresh once (the refresh cookie still
      // works after the access token expired) so the next automatic retry authenticates.
      if (!this._authRefreshInFlight && this._authService.token?.accessToken) {
        this._authRefreshInFlight = true;
        this._authService.refreshToken(true, true, true).subscribe({
          next: () => (this._authRefreshInFlight = false),
          error: () => (this._authRefreshInFlight = false)
        });
      }
    });

    this._socket.on('modbus-data', (data) => {
      if (data) {
        this._data.set(data);
      }
    });

    this._socket.on('operator/status', (data: IOperatorStatusPayload) => {
      if (data) this._operatorStatus$.next(data);
    });

    this._socket.on('operator/heartbeat', (data: IOperatorStatusPayload) => {
      if (data) this._operatorHeartbeat$.next(data);
    });

    this._socket.on('operator/metrics', (data: IOperatorMetricsPayload) => {
      if (data) this._operatorMetrics$.next(data);
    });
  }

  public initialize(): void {
    this._customerOperatorDeviceService.selectedCustomerOperatorDevice$.subscribe({
      next: (device) => {
        this.disconnect();
        if (device) {
          this.connect();
        }
      }
    });
  }

  public connect(): void {
    this._initWs();
    this._socket?.connect();
  }

  public disconnect(): void {
    if (this._socket) {
      this._socket.removeAllListeners();
      this._socket.disconnect();
      this._socket = undefined;
    }
    this._authRefreshInFlight = false;
    this._connect$.next(false);
  }

  public get data$(): Observable<any> {
    return this._data$;
  }

  public get operatorStatus$(): Observable<IOperatorStatusPayload | null> {
    return this._operatorStatus$.asObservable();
  }

  public get operatorHeartbeat$(): Observable<IOperatorStatusPayload | null> {
    return this._operatorHeartbeat$.asObservable();
  }

  public get operatorMetrics$(): Observable<IOperatorMetricsPayload | null> {
    return this._operatorMetrics$.asObservable();
  }
}
